import React from 'react';
import styled from 'styled-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Col, Row } from 'react-bootstrap';
import { FormTypes, sectionList } from '../../constants/queryFragments';
import loadable from '@loadable/component';
import {
  Body,
  HeaderH2,
  HeaderH3,
  MainContainer,
  ParaText,
  HeadingH4,
  UnorderedList,
  LinkStyle,
  ListItem,
  HorizontalBreak,
} from '../../styles';
import isExternalUrl, { addTrailingSlash, isExternalUrlHref } from '../../utils';

const TightButtonStack = styled.div`
  position: relative;
  padding-top: 28px;
  padding-bottom: 28px;

  /* Tight button stack */
  p:has(> a) {
    margin-bottom: 6px;
  }
  p > a {
    margin-bottom: 0 !important;
  }

  /* Kill trailing margin so each row is bottom-balanced */
  > *:last-child {
    margin-bottom: 0 !important;
  }

  ${(props) =>
    props.$hasDivider &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 25%;
      width: 50%;
      height: 1px;
      background: rgba(0, 0, 0, 0.15);
    }
  `}

  /* In horizontal 3-column layout (md), suppress divider + extra padding */
  @media (min-width: 768px) and (max-width: 1199px) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;

    &::before {
      display: none !important;
    }
  }
`;

const ColumnListWith3Items = ({ sectionData }) => {
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    setLoaded(true);
  }, []);
  const optionsMainStyle = {
    renderNode: {
      [BLOCKS.HEADING_2]: (node, children) => (
        <HeaderH2 textAlign="left">{children}</HeaderH2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <HeaderH3 textAlign="left">{children}</HeaderH3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => <HeadingH4>{children}</HeadingH4>,
      [BLOCKS.PARAGRAPH]: (node, children) => <ParaText>{children}</ParaText>,
      [BLOCKS.UL_LIST]: (node, children) => (
        <UnorderedList>{children}</UnorderedList>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => <ListItem>{children}</ListItem>,
      [INLINES.HYPERLINK]: (node, children) => (
        <LinkStyle
          href={
            isExternalUrlHref(node?.data?.uri)
              ? node?.data?.uri
              :addTrailingSlash(
                 process.env.GATSBY_DOMAIN_URL + node?.data?.uri
              )
          }
          target={isExternalUrl(node?.data?.uri) ? '_blank' : '_self'}
        >
          {children}
        </LinkStyle>
      ),
    },
    renderText: (text) =>
      text.split('\n').flatMap((text, i) => [i > 0 && <br key={i} />, text]),
  };
  return !loaded ? (
    <MainContainer>
      <Body>
        {sectionData?.header?.trim() && (
          <HeaderH2 style={{ textAlign: 'center' }}>
            {sectionData?.header}
          </HeaderH2>
        )}
        {sectionData?.subtext?.trim() && (
          <HeaderH3>{sectionData?.subtext}</HeaderH3>
        )}
        {sectionData?.listDetails &&
          documentToReactComponents(
            JSON.parse(sectionData?.listDetails?.raw),
            optionsMainStyle
          )}
        {sectionData && (
          <>
            <Row>
              {sectionData?.list &&
                sectionData?.list?.map((section, index) => {
                  if (
                    section?.type &&
                    sectionList?.includes(section?.type) &&
                    !FormTypes?.includes(section?.type)
                  ) {
                    // const sectionData = sectionData.list[section.type];
                    const SectionComponent = loadable(() =>
                      import(
                        `../../components/${section?.type}/${section?.type}`
                      )
                    );
                    return (
                      <Col xs={12} lg={4} key={index}>
                        <SectionComponent sectionData={section} />
                      </Col>
                    );
                  }
                })}
            </Row>
            <HorizontalBreak />
          </>
        )}
      </Body>
    </MainContainer>
  ) : (
    <MainContainer>
      <Body>
        {sectionData?.header?.trim() && (
          <HeaderH2 style={{ textAlign: 'center' }}>
            {sectionData?.header}
          </HeaderH2>
        )}
        {sectionData?.subtext?.trim() && (
          <HeaderH3>{sectionData?.subtext}</HeaderH3>
        )}
        {sectionData?.listDetails &&
          documentToReactComponents(
            JSON.parse(sectionData?.listDetails?.raw),
            optionsMainStyle
          )}
        {sectionData && (
          <>
            {(() => {
              const validItems = sectionData?.list?.filter(
                (s) => s?.type && sectionList?.includes(s?.type)
              ) || [];
              const formItems = validItems.filter((s) => FormTypes?.includes(s?.type));
              const nonFormItems = validItems.filter((s) => !FormTypes?.includes(s?.type));

              if (formItems.length > 0) {
                return (
                  <Row>
                    <Col xs={12} xl={6}>
                      {formItems.map((section, index) => {
                        const SectionComponent = loadable(() =>
                          import(`../../components/${section?.type}/${section?.type}`)
                        );
                        return (
                          <SectionComponent key={`form-${index}`} sectionData={section} />
                        );
                      })}
                    </Col>
                    <Col xs={12} xl={6}>
                      <Row>
                        {nonFormItems.map((section, index) => {
                          const SectionComponent = loadable(() =>
                            import(`../../components/${section?.type}/${section?.type}`)
                          );
                          return (
                            <Col xs={12} md={4} xl={12} key={`item-${index}`}>
                              <TightButtonStack $hasDivider={index > 0}>
                                <SectionComponent sectionData={section} />
                              </TightButtonStack>
                            </Col>
                          );
                        })}
                      </Row>
                    </Col>
                  </Row>
                );
              }

              return (
                <Row>
                  {validItems.map((section, index) => {
                    const SectionComponent = loadable(() =>
                      import(`../../components/${section?.type}/${section?.type}`)
                    );
                    return (
                      <Col xs={12} lg={4} key={index}>
                        <SectionComponent sectionData={section} />
                      </Col>
                    );
                  })}
                </Row>
              );
            })()}
            <HorizontalBreak />
          </>
        )}
      </Body>
    </MainContainer>
  );
};
export default ColumnListWith3Items;
